'use client'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn, normalizeSlug } from '@/lib/utils'
import Autoplay from 'embla-carousel-autoplay'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import type { TCategoryTilesByGender } from './category-data'
import {
  DEFAULT_GENDER,
  GENDER_LINKS,
  TGender,
  TRACKING_PREFIX,
} from './constants'
import {
  GenderTabs,
  IMAGE_GRADIENT_CLASS,
  SECTION_TITLE_CLASS,
  SECTION_X_CLASS,
  ViewAllLink,
} from './ui'

const TRACKING = `${TRACKING_PREFIX}__category`

type Props = {
  /** Danh mục Nam/Nữ lấy từ CMS (homepage_men / homepage_women → homepage_category) */
  tilesByGender: TCategoryTilesByGender
}

const getValidTiles = (tiles?: TCategoryTilesByGender[TGender]) =>
  (tiles ?? []).filter((item) => !item.hidden && Boolean(item.image))

export function CategorySection({ tilesByGender }: Props) {
  const t = useTranslations('do_thu_dong_hardcode')
  const [gender, setGender] = useState<TGender>(DEFAULT_GENDER)
  const tiles = getValidTiles(tilesByGender[gender])

  // Không có data CMS cho cả Nam lẫn Nữ thì ẩn section
  if (
    !tiles.length &&
    !getValidTiles(tilesByGender.MALE).length &&
    !getValidTiles(tilesByGender.FEMALE).length
  ) {
    return null
  }

  const viewAll = (
    <ViewAllLink
      href={GENDER_LINKS[gender]}
      label={t('view_all')}
      trackingValue={`${TRACKING}__view-all__${gender.toLowerCase()}`}
    />
  )

  return (
    <section
      className={cn(
        'flex flex-col gap-5 py-6 lg:gap-10 lg:py-10',
        SECTION_X_CLASS
      )}
    >
      <div className='flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between'>
        <div className='flex items-end gap-10'>
          <h2 className={SECTION_TITLE_CLASS}>{t('category.title')}</h2>
          <div className='pb-1 max-lg:hidden'>
            <GenderTabs
              value={gender}
              onChange={setGender}
              labels={{ MALE: t('gender.male'), FEMALE: t('gender.female') }}
              trackingPrefix={TRACKING}
            />
          </div>
        </div>
        <div className='flex items-start justify-between pb-1 lg:hidden'>
          <GenderTabs
            value={gender}
            onChange={setGender}
            labels={{ MALE: t('gender.male'), FEMALE: t('gender.female') }}
            trackingPrefix={TRACKING}
          />
          {viewAll}
        </div>
        <div className='max-lg:hidden'>{viewAll}</div>
      </div>

      <Carousel
        key={gender}
        opts={{ align: 'start', loop: true }}
        plugins={[Autoplay({ delay: 3000, stopOnInteraction: false })]}
      >
        <CarouselContent className='-ml-2 lg:-ml-5'>
          {tiles.map((tile, index) => {
            const label = tile.title
            return (
              <CarouselItem
                key={`${tile.title}-${index}`}
                className='basis-[148px] pl-2 md:basis-1/5 lg:pl-5 2xl:basis-[calc(100%/7)]'
              >
                <Link
                  href={'/' + normalizeSlug(tile.url)}
                  ga-tracking-value={`${TRACKING}__${gender.toLowerCase()}__${index + 1}`}
                  ga-tracking-label={label}
                  className='group relative flex aspect-[140/202] flex-col justify-end overflow-hidden rounded-lg bg-neutral-400 p-4 lg:aspect-[240/300]'
                >
                  {tile.image ? (
                    <>
                      <Image
                        src={ImageHelper.getImageUrl(tile.image, '', 520)}
                        alt={label}
                        fill
                        sizes='(min-width: 1536px) 14vw, (min-width: 768px) 20vw, 148px'
                        className='object-cover transition-transform duration-300 group-hover:scale-105'
                        loading={index < 4 ? 'eager' : 'lazy'}
                      />
                      <div className={IMAGE_GRADIENT_CLASS} />
                    </>
                  ) : null}
                  <span className='relative font-criteria text-lg leading-[26px] tracking-[-0.02em] text-white'>
                    {label}
                  </span>
                </Link>
              </CarouselItem>
            )
          })}
        </CarouselContent>
      </Carousel>
    </section>
  )
}
